import { Agent } from './agent-types';
import { FeedbackAnswer, FeedbackQuestion } from './feedback-types';
import { ImageRegistration, MunchedImage } from './image-muncher-types';
import {
  JourneyExpandPolicies,
  JourneyInitialDisplayModes,
  JourneyEntryTypes,
  VisitorJourneyStepStatus,
} from './journey-types';

export interface ChatMessage {
  id: string;
  from: string | number;
  visitorId: string;
  agent: Agent;
  text: string;
  content: ChatMessageContent;
  mentionedUserIds?: number[];
  timestamp: number;
  pending?: boolean;
  imageIds?: string[];
  renderedText?: string;
  images?: MunchedImage[];
  conversationId: number;
  sourceType: 'agent' | 'bot' | 'visitor' | 'system' | 'ai';
  avatarSessionId?: string;
  textStreamRendered?: boolean;
  dateRead: string | null;
  moderationStatus: 'pending' | 'approved' | 'rejected' | null;
  overrideModerationStatus: boolean;
}

export type ChatMessageArgs = Pick<
  ChatMessage,
  'content' | 'mentionedUserIds' | 'imageIds' | 'conversationId'
> & {
  visitorId?: string;
  registeredImg?: ImageRegistration;
  file?: Blob;
};

export interface ChatMessageContent {
  blocks: AnyChatMessageBlock[];
}

export enum ChatMessageBlockType {
  TEXT = 'text',
  ANSWER_BUTTONS = 'answer_buttons',
  TYPING = 'typing',
  FULL_NAME_INPUT = 'full_name_input',
  FIRST_NAME_INPUT = 'first_name_input',
  LAST_NAME_INPUT = 'last_name_input',
  EMAIL_INPUT = 'email_input',
  PHONE_INPUT = 'phone_input',
  SHORT_INPUT = 'short_input',
  CHOICES = 'choices',
  FEEDBACK_INPUT = 'feedback_input',
  SCHEDULE = 'schedule',
  VIDEO = 'video',
  JOURNEY_EVENT = 'journey_event',
  GENERAL_EVENT = 'general_event',
}

export interface ChatMessageBlock<T extends ChatMessageBlockType> {
  type: T;
}
export interface ChatMessageJourneyBlockFields {
  journeyStepId?: number;
  journeyStepStatus?: VisitorJourneyStepStatus;

  journeyOptionEntryType?: JourneyEntryTypes;
  journeyOptionExpandPolicy?: JourneyExpandPolicies;
  journeyOptionInitialDisplayMode?: JourneyInitialDisplayModes;
  journeyOptionQuestionIsMandatory?: boolean;

  journeyStale?: boolean;
  journeyCanceled?: boolean;
}
export interface ChatMessageTextBlock
  extends ChatMessageBlock<ChatMessageBlockType.TEXT>,
    ChatMessageJourneyBlockFields {
  text: string;
}
export type ChatMessageAnswerButtonsBlock =
  ChatMessageBlock<ChatMessageBlockType.ANSWER_BUTTONS>;

export type ChatMessageTypingBlock = ChatMessageBlock<ChatMessageBlockType.TYPING>;

export interface ChatMessageFullNameInputBlock
  extends ChatMessageBlock<ChatMessageBlockType.FULL_NAME_INPUT> {
  fullNameInput: string | null;
}

export type ChatMessageJourneyFullNameInputBlock = ChatMessageFullNameInputBlock &
  ChatMessageJourneyBlockFields;

export interface ChatMessageFirstNameInputBlock
  extends ChatMessageBlock<ChatMessageBlockType.FIRST_NAME_INPUT> {
  firstNameInput: string | null;
}

export type ChatMessageJourneyFirstNameInputBlock = ChatMessageFirstNameInputBlock &
  ChatMessageJourneyBlockFields;

export interface ChatMessageLastNameInputBlock
  extends ChatMessageBlock<ChatMessageBlockType.LAST_NAME_INPUT> {
  lastNameInput: string | null;
}

export type ChatMessageJourneyLastNameInputBlock = ChatMessageLastNameInputBlock &
  ChatMessageJourneyBlockFields;

export interface ChatMessageEmailInputBlock
  extends ChatMessageBlock<ChatMessageBlockType.EMAIL_INPUT> {
  emailInput: string | null;
}

export type ChatMessageJourneyEmailInputBlock = ChatMessageEmailInputBlock &
  ChatMessageJourneyBlockFields;

export interface ChatMessagePhoneInputBlock
  extends ChatMessageBlock<ChatMessageBlockType.PHONE_INPUT>,
    ChatMessageJourneyBlockFields {
  phoneInput: string | null;
}

export interface ChatMessageShortInputBlock
  extends ChatMessageBlock<ChatMessageBlockType.SHORT_INPUT>,
    ChatMessageJourneyBlockFields {
  shortInput: string | null;
  label: string | null;
}

export interface ChatMessageFeedbackInputBlock
  extends ChatMessageBlock<ChatMessageBlockType.FEEDBACK_INPUT> {
  feedbackAnswers: Record<number, FeedbackAnswer>;
  feedbackQuestions: Record<number, FeedbackQuestion>;
}

export interface ChatMessageChoicesBlock
  extends ChatMessageBlock<ChatMessageBlockType.CHOICES>,
    ChatMessageJourneyBlockFields {
  options: { label: string; value?: string }[];
  choice: string | null;
}

export interface ChatMessageScheduleBlock
  extends ChatMessageBlock<ChatMessageBlockType.SCHEDULE> {
  calendarEventId: number | null;
  calendarEventEmailAddress: string | null;
  calendarEventStartTime: string | null;
  calendarEventTypeId: number;
  agent: Agent;
}

export type ChatMessageJourneyScheduleBlock = ChatMessageScheduleBlock &
  ChatMessageJourneyBlockFields;

export interface ChatMessageVideoBlock
  extends ChatMessageBlock<ChatMessageBlockType.VIDEO>,
    ChatMessageJourneyBlockFields {
  allowSkip: boolean;
  journeyVideoId: string;
  immediatelyProceed?: boolean;
}

export enum ChatMessageJourneyEventType {
  INBOUND_CALL = 'inbound_call',
}
export interface ChatMessageJourneyEventBlock
  extends ChatMessageBlock<ChatMessageBlockType.JOURNEY_EVENT>,
    ChatMessageJourneyBlockFields {
  journeyEventType: ChatMessageJourneyEventType;
}

export interface ChatMessageInboundCallBlock extends ChatMessageJourneyEventBlock {
  journeyEventType: ChatMessageJourneyEventType.INBOUND_CALL;
  waitTimeSeconds: number;
}

export enum ChatMessageGeneralEventType {
  AGENT_CALL = 'agent_call',
  AGENT_TRANSFER = 'agent_transfer',
  VISITOR_MISSED_AGENT_CALL = 'visitor_missed_agent_call',
  VISITOR_CALLBACK_AGENT = 'visitor_callback_agent',
}
export interface ChatMessageGeneralEventBlock
  extends ChatMessageBlock<ChatMessageBlockType.GENERAL_EVENT> {
  generalEventType: ChatMessageGeneralEventType;
}
export interface ChatMessageGeneralEventAgentCallBlock
  extends ChatMessageGeneralEventBlock {
  generalEventType: ChatMessageGeneralEventType.AGENT_CALL;
  agent: Agent;
}
export interface ChatMessageGeneralEventAgentTransferBlock
  extends ChatMessageGeneralEventBlock {
  generalEventType: ChatMessageGeneralEventType.AGENT_TRANSFER;
  agent: Agent;
}
export interface ChatMessageGeneralEventVisitorMissedAgentCallBlock
  extends ChatMessageGeneralEventBlock {
  generalEventType: ChatMessageGeneralEventType.VISITOR_MISSED_AGENT_CALL;
  agent: Agent;
}
export interface ChatMessageGeneralEventVisitorCallbackAgentBlock
  extends ChatMessageGeneralEventBlock {
  generalEventType: ChatMessageGeneralEventType.VISITOR_CALLBACK_AGENT;
  agent: Agent;
}

export type AnyChatMessageBlock =
  | ChatMessageTextBlock
  | ChatMessageAnswerButtonsBlock
  | ChatMessageTypingBlock
  | ChatMessageFullNameInputBlock
  | ChatMessageFirstNameInputBlock
  | ChatMessageLastNameInputBlock
  | ChatMessageEmailInputBlock
  | ChatMessageJourneyFullNameInputBlock
  | ChatMessageJourneyFirstNameInputBlock
  | ChatMessageJourneyLastNameInputBlock
  | ChatMessageJourneyEmailInputBlock
  | ChatMessagePhoneInputBlock
  | ChatMessageShortInputBlock
  | ChatMessageFeedbackInputBlock
  | ChatMessageChoicesBlock
  | ChatMessageScheduleBlock
  | ChatMessageJourneyScheduleBlock
  | ChatMessageVideoBlock
  | ChatMessageJourneyEventBlock
  | ChatMessageGeneralEventBlock;

export type ChatMessageErrorCode = 'UNKNOWN' | 'NOT_ASSOCIATED' | 'INVALID';

export interface ChatMessagesPage {
  items: ChatMessage[];
  cursor: null | number;
}
